import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { useRecoilState, useSetRecoilState } from "recoil";

import { Base64EncodedImage, PropertyCategory, PropertyTemporary, PropertyType } from "@/api/generated";
import { Loading } from "@/components/Loading";
import {
  focusRegistrationField,
  getRegistrationFieldStep,
} from "@/components/property_registration/RegistrationActions";
import { DraftPropertyModal } from "@/components/property_registration/RegistrationDraft";
import { DraftPropertyReuseModal } from "@/components/property_registration/RegistrationDraft";
import { RegistrationLayout } from "@/components/property_registration/RegistrationLayout";
import { getRegistrationStepFields } from "@/components/property_registration/registrationStepFields";
import { DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA, DateFieldNames, DoubleFieldNames } from "@/constants/addNewProperty";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { AddNewPropertyFormSchema } from "@/types/addNewProperty";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { castFieldInputs } from "@/utilities/castFieldInputs";
import { deleteUnacceptableKeys } from "@/utilities/deleteUnacceptableKeys";
import { fileToBase64EncodedImage } from "@/utilities/fileToBase64EncodedImage";
import {
  formDataMappingToLeaseContract,
  formDataMappingToLeaseContractForUpdate,
  formDataMappingToProperty,
  formDataMappingToPropertyBuildingRoom,
  handleFirstRegistration,
  postProperty,
} from "@/utilities/propertyManagement";
import { getStoragePropertyRegistrationState, removeStoragePropertyRegistrationState } from "@/utilities/storage";
import { addNewBuildingPropertySchema, AddNewBuildingPropertyFormData } from "@/validations/management/registration";
import {
  PropertyManagementListSnackbarState,
  PropertyRegistrationState,
  UserDataFirstRegisteredStatusState,
  useRequestInitialLoading,
} from "@/view_models/atoms";

import AddNewOwnBuildingPropertyBasicPage from "./components/Basic";
import AddNewOwnBuildingPropertyCostPage from "./components/Cost";
import AddNewOwnBuildingPropertyLoanPage from "./components/Loan";
import AddNewOwnBuildingPropertyRoomPage from "./components/Room";

export const ManagementAddNewOwnBuildingPropertyPage: React.FC = () => {
  const isSubmittingRef = React.useRef(false);
  const methods = useForm<AddNewBuildingPropertyFormData>({
    mode: "onTouched",
    shouldUnregister: false,
    // エラー項目への移動はonInvalidで行い、閉じたシートのrefへのフォーカスを防ぐ。
    shouldFocusError: false,
    resolver: zodResolver(addNewBuildingPropertySchema),
  });
  const history = useHistory();
  const requestInitialLoading = useRequestInitialLoading();

  const setManagementListSnackBarState = useSetRecoilState(PropertyManagementListSnackbarState);
  const setUserDataFirstRegisteredStatus = useSetRecoilState(UserDataFirstRegisteredStatusState);
  const [isOpenReuseTempoModal, setIsOpenReuseTempoModal] = React.useState(false);

  // LocalStorage
  const [formPersistData, setFormPersistData] = useRecoilState(PropertyRegistrationState);
  const localStorageFormData = getStoragePropertyRegistrationState();
  // フォームの状態
  const [formData, setFormData] = React.useState<AddNewPropertyFormSchema>(formPersistData);

  const [currentStep, setCurrentStep] = React.useState(0);

  const [isClickedPageBack, setIsClickedPageBack] = React.useState(false);
  // 一時保存の状態
  const [propertyTempInfo, setPropertyTempInfo] = React.useState<PropertyTemporary>(null);

  // 初期レンダリング
  // ローカルストレージのデータがある場合は確認モーダルの表示
  React.useEffect(() => {
    const fp = PropallyApiFactory();
    const fetchPropertyRegistrationTemporary = async () => {
      try {
        const response = await fp.v1PropertyTemporaryGet({ withCredentials: true });
        if (response.status === 200) {
          if (response.data?.updated_at && localStorageFormData !== null) {
            setIsOpenReuseTempoModal(true);
            setPropertyTempInfo(response.data);
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchPropertyRegistrationTemporary();
  }, []);

  // 一時保存関数
  const saveTemporary = async () => {
    await methods.trigger();
    // DBに一時保存データがあるかどうかフラグを保存
    const fp = PropallyApiFactory();
    fp.v1PropertyTemporaryPost({ withCredentials: true }).then(() => {
      console.log("一時保存しました");
    });

    // ローカルストレージの更新
    setFormPersistData({ ...formData, ...methods.getValues() } as AddNewPropertyFormSchema);
  };

  const onClickPageNext = React.useCallback(() => {
    window.scrollTo(0, 0);
    setCurrentStep((prev) => prev + 1);
  }, []);

  const onClickPageBack = React.useCallback(() => {
    window.scrollTo(0, 0);
    setCurrentStep((prev) => prev - 1);
  }, []);

  const onClickBack = React.useCallback(() => {
    window.scrollTo(0, 0);
    setIsClickedPageBack(true);
  }, []);

  // ==== 一時保存情報の利用 ====
  const onClickReuseTempo = async () => {
    setIsOpenReuseTempoModal(false);
  };
  // ==== 物件一時保存情報削除 ====
  const deletePropertyTemporary = async () => {
    const fp = PropallyApiFactory();
    try {
      const response = await fp.v1PropertyTemporaryDelete({ withCredentials: true });
      if (response.status === 200) {
        // フォームのデフォルト値を削除
        setFormData(DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA);
        setFormPersistData(DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA);
        // ローカルストレージの削除
        removeStoragePropertyRegistrationState();
        // フォームの値をリセット
        methods.reset(DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA);
        // 一時保存情報の削除
        setPropertyTempInfo(null);
      }
    } catch (e) {
      console.error(e);
    }
    setIsOpenReuseTempoModal(false);
  };

  const saveTemporaryAndGoBack = async () => {
    await saveTemporary();
    history.push("/management/own");
  };
  const cancelSaveTemporaryAndGoBack = async () => {
    await deletePropertyTemporary();
    history.push("/management/own");
  };
  // 各ページでの処理
  const validate = async () => {
    const isValid = await methods.trigger(getRegistrationStepFields("building", currentStep));
    if (isValid) {
      const saveFormData = {
        ...formData,
        ...methods.getValues(),
      } as AddNewPropertyFormSchema;
      console.log("saveFormData", saveFormData);
      console.log("image", saveFormData.property_image);
      setFormData(saveFormData);
      // LocalStorage
      setFormPersistData(saveFormData);
      console.log("valid");

      let base64_encoded_image: Base64EncodedImage;
      const image = saveFormData.property_image;

      if (image instanceof File) {
        base64_encoded_image = await fileToBase64EncodedImage(image);
      }
      // delete saveFormData.property_image;
      console.log("base64_encoded_image", base64_encoded_image);
    }
    return isValid;
  };

  const onSubmit = React.useCallback(async () => {
    console.log("submit");
    // 二重送信防止
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    // LocalStorage
    setFormPersistData(formPersistData);

    console.log("saved");

    const fp = PropallyApiFactory();

    let data = {
      ...methods.getValues(),
      ...formData,
    };
    console.log("data", data);

    let base64_encoded_image: Base64EncodedImage;
    // 画像のアップロード処理
    const image = data.property_image as File;

    if (image && image instanceof File) {
      base64_encoded_image = await fileToBase64EncodedImage(image);
    }
    delete data.property_image;

    // cut off the undefined/non-entered values
    data = deleteUnacceptableKeys(data);

    // cast date & double fields
    data = castFieldInputs(data, { dateFieldNames: DateFieldNames, doubleFieldNames: DoubleFieldNames });

    // Property型に変換
    const property = formDataMappingToProperty(data, PropertyType.NUMBER_0, PropertyCategory.一棟物件);

    console.log("property", property);

    // LeaseContract型に変換
    const lease_contract = formDataMappingToLeaseContract(data);

    // PropertyBuildingRoom型に変換
    const building_room = formDataMappingToPropertyBuildingRoom(data);

    // PropertyBuilder型に変換
    const property_builder = formDataMappingToLeaseContractForUpdate(
      data,
      property,
      lease_contract,
      building_room,
      PropertyCategory.一棟物件
    );

    console.log("property_builder", property_builder);

    // --- Client-side validation (prevent 400/422 before sending) ---
    try {
      const { validateProperty } = await import("@/utilities/validateProperty");
      const result = validateProperty(property_builder);
      console.log("[Register] validateProperty", {
        valid: result.valid,
        errors: result.errors,
        issues: result.issues,
      });
      if (!result.valid) {
        const knownFields = new Set([
          "name",
          "bought_at",
          "structure",
          "total_price_of_property",
          "loan_amount",
          "payment_starts_at",
          "payment_period",
          "management_type",
          "sales_status",
          "property_type",
          "property_category",
          "prefecture",
          "city",
          "town",
          "railway",
          "station",
          // lease_contract (sent but optional to validate for this category)
          "monthly_fee",
          "monthly_management_fee",
          "starts_at",
          "ends_at",
          "key_money",
          "security_deposit",
          "type_of_the_income_of_the_beginning_of_contract",
          // first month cashflow
          "management_cost",
          "building_management_cost",
          "repair_fund_cost",
          "misc_cost",
          "loan_prepayment_method",
          "misc_onetime_cost_contents",
          "outsourcing_fee_of_new_lease_contract",
          "outsourcing_fee_of_updating_lease_contract",
        ]);
        Object.entries(result.errors).forEach(([field, message]) => {
          if (knownFields.has(field)) {
            methods.setError(field as any, { type: "manual", message });
          }
        });
        console.warn("[Register] Blocked by client validation.", result.errors);
        isSubmittingRef.current = false;
        window.scrollTo(0, 0);
        return;
      }
      console.log("[Register] Client validation passed.");
    } catch (e) {
      console.error("validateProperty load/exec error", e);
    }

    try {
      console.log("[Register] Posting property...", {
        total_price_of_property: property_builder.total_price_of_property,
        loan_amount: property_builder.loan_amount,
        payment_period: property_builder.payment_period,
        bought_at: property_builder.bought_at,
        payment_starts_at: property_builder.payment_starts_at,
      });
      console.log(
        "[Register] LeaseContract dates",
        property_builder.lease_contract && {
          starts_at: property_builder.lease_contract.starts_at,
          ends_at: property_builder.lease_contract.ends_at,
        }
      );
      const postResponse = await postProperty(property_builder);

      console.log("postResponse", postResponse);
      if (base64_encoded_image) {
        await fp.v1PropertiesPropertyIdImagesPost(postResponse.data.id, base64_encoded_image, {
          withCredentials: true,
        });
      }

      // 物件一時保存情報削除
      await fp.v1PropertyTemporaryDelete({ withCredentials: true });

      await handleFirstRegistration(setUserDataFirstRegisteredStatus);
    } catch (e) {
      console.error(e);
      // 物件一覧ページでのスナックバー表示用
      setManagementListSnackBarState({ isOpen: true, message: "物件を追加できませんでした", status: "error" });
      history.push("/management/own");
      return;
    }

    // if(!isEdit && String(property.property_type) === String(PROPERTY_TYPE_VALUES.owned)) {
    //   // 新規登録 且つ 保有物件の場合のみエージェントに新着通知
    //   await fp.v1NotificationsToAgentForNewPropertyPost( { withCredentials: true });
    // }

    // データの初期化
    setFormData(DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA);
    setFormPersistData(DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA);
    // ローカルストレージの物件情報の削除
    removeStoragePropertyRegistrationState();
    console.log("success");
    // 物件一覧ページでのスナックバー表示用
    setManagementListSnackBarState({ isOpen: true, message: "物件を追加しました", status: "success" });
    requestInitialLoading();

    sendEventToAppMeasurementTool(AppEventTokenEnum.OwnPropertyCompleted);

    history.push("/management/own");

    isSubmittingRef.current = false; // 送信完了後、二重送信管理フラグをリセット
    console.log("complete");
  }, [formData]);

  const onInvalid = (errors: Record<string, unknown>) => {
    const field = Object.keys(errors)[0];
    if (!field) return;
    setCurrentStep(getRegistrationFieldStep("building", field));
    focusRegistrationField(field);
  };

  const steps = [
    <AddNewOwnBuildingPropertyBasicPage key="basic" onNext={onClickPageNext} validate={validate} />,
    <AddNewOwnBuildingPropertyLoanPage
      key="loan"
      onNext={onClickPageNext}
      onBack={onClickPageBack}
      validate={validate}
    />,
    <AddNewOwnBuildingPropertyRoomPage
      key="room"
      onNext={onClickPageNext}
      onBack={onClickPageBack}
      validate={validate}
    />,
    <AddNewOwnBuildingPropertyCostPage key="cashflow" onBack={onClickPageBack} validate={validate} />,
  ];
  const stepText = [
    {
      title: "建物の概要",
      message: "お手元の売買契約書や重要事項説明書を参考にしてください。",
    },
    {
      title: "価格とローン",
      message: "売買契約書や返済予定表を参考にしてください。",
    },
    {
      title: "部屋の家賃・契約",
      message: "収入に関する情報を記入してください",
    },
    {
      title: "建物全体の経費",
      message: "支出に関する情報を記入してください",
    },
  ];

  // ローカルストレージのデータをフォームにセットしてレンダリングをする前にモーダルで確認
  if (isOpenReuseTempoModal) {
    return (
      <RegistrationLayout onBack={onClickBack} title="物件の概要">
        <Loading />
        <DraftPropertyReuseModal
          onClose={() => setIsOpenReuseTempoModal(false)}
          onClickUse={onClickReuseTempo}
          onClickDelete={deletePropertyTemporary}
          lastSavedDate={dayjs(propertyTempInfo?.updated_at).format("YYYY年MM月DD日 HH:mm")}
        />
      </RegistrationLayout>
    );
  }

  return (
    <RegistrationLayout
      onBack={onClickBack}
      building
      step={currentStep}
      title={stepText[currentStep].title}
      description={stepText[currentStep].message}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>{steps[currentStep]}</form>
      </FormProvider>
      {isClickedPageBack ? (
        <DraftPropertyModal
          onClose={() => setIsClickedPageBack(false)}
          onClickSave={saveTemporaryAndGoBack}
          onClickCancel={cancelSaveTemporaryAndGoBack}
        />
      ) : null}
    </RegistrationLayout>
  );
};
